import { useState, useCallback } from 'react';
import type { IUserService } from '../../application/services/UserService';
import type { User, CreateUserRequest } from '../../domain/entities/User';

export const useUserService = (userService: IUserService) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createUser = useCallback(async (userData: CreateUserRequest): Promise<boolean> => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await userService.createUser(userData);
      if (result.success) {
        setUsers(prev => [...prev, result.data]);
        return true;
      } else {
        setError(result.error.message);
        return false;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      return false;
    } finally {
      setLoading(false);
    }
  }, [userService]);

  const getUserById = useCallback(async (id: string): Promise<User | null> => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await userService.getUserById(id);
      if (result.success) {
        return result.data;
      } else {
        setError(result.error.message);
        return null;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      return null;
    } finally {
      setLoading(false);
    }
  }, [userService]);

  const getUserByEmail = useCallback(async (email: string): Promise<User | null> => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await userService.getUserByEmail(email);
      if (result.success) {
        return result.data;
      } else {
        setError(result.error.message);
        return null;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      return null;
    } finally {
      setLoading(false);
    }
  }, [userService]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    users,
    loading,
    error,
    createUser,
    getUserById,
    getUserByEmail,
    clearError,
  };
};
